<template>
  <!-- A mini Ad Rank leaderboard: the brand's top three ads by impressions.
  Context: brandName, adThumbnails (optional, up to 3) -->
  <div>
    <!-- Window header -->
    <div class="flex items-center gap-2 px-3.5 py-2 border-b border-border-normal">
      <AdRankIcon
        :width="14"
        :height="14"
        class="text-primary-red-100"
      />
      <BaseText
        type="label"
        size="xs"
        class="text-text-muted truncate"
      >
        Ad Rank · {{ brandName }}
      </BaseText>
      <BaseText
        type="body"
        size="xs"
        class="ml-auto text-text-subdued flex-shrink-0"
      >
        Last 30 days
      </BaseText>
    </div>
    <!-- Rows -->
    <div class="px-3.5 py-2.5 flex flex-col gap-2">
      <div
        v-for="(row, index) in rows"
        :key="`rank-${index}`"
        class="flex items-center gap-2.5"
      >
        <div
          class="scene-rank flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
          :class="index === 0 ? 'scene-rank--top' : 'bg-neutral-25'"
        >
          <BaseText
            type="label"
            size="xs"
            :class="index === 0 ? 'text-white' : 'text-text-muted'"
          >
            {{ index + 1 }}
          </BaseText>
        </div>
        <div class="scene-thumb flex-shrink-0 rounded overflow-hidden bg-neutral-50">
          <img
            v-if="row.thumbnail"
            :src="row.thumbnail"
            alt=""
            class="w-full h-full object-cover"
          >
          <div
            v-else
            class="w-full h-full scene-thumb-placeholder"
            :class="`scene-thumb-placeholder--${index}`"
          />
        </div>
        <div class="min-w-0 flex-grow">
          <BaseText
            type="label"
            size="xs"
            class="text-text-loud truncate"
          >
            {{ row.label }}
          </BaseText>
          <div class="mt-1 h-1.5 rounded-full bg-neutral-25 overflow-hidden">
            <div
              class="scene-bar h-full rounded-full"
              :style="{ width: `${row.width}%`, animationDelay: `${180 + index * 90}ms` }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdRankIcon from '../../globals/Icons/SpyderModeIcons/AdRankIcon.vue'

// Illustrative ranking; thumbnails come from the context when the tour saw
// real ads, otherwise soft gradient placeholders
const ROWS = [
  { label: 'Top ad', width: 100 },
  { label: 'Evergreen', width: 68 },
  { label: 'Breakout', width: 41 }
]

export default {
  name: 'AdRankScene',
  components: {
    AdRankIcon
  },
  props: {
    context: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    brandName () {
      return this.context.brandName || 'Top ads'
    },
    rows () {
      const thumbnails = (this.context.adThumbnails || []).filter(Boolean)
      return ROWS.map((row, index) => ({ ...row, thumbnail: thumbnails[index] || null }))
    }
  }
}
</script>

<style scoped>
.scene-rank--top {
  background-color: theme('colors.primary.red-100');
}
.scene-thumb {
  width: 28px;
  height: 34px;
}
.scene-thumb-placeholder--0 {
  background: linear-gradient(160deg, theme('colors.primary.red-25') 0%, theme('colors.primary.red-50') 100%);
}
.scene-thumb-placeholder--1 {
  background: linear-gradient(160deg, theme('colors.primary.blue-25') 0%, theme('colors.primary.blue-50') 100%);
}
.scene-thumb-placeholder--2 {
  background: linear-gradient(160deg, theme('colors.primary.purple-25') 0%, theme('colors.primary.purple-50') 100%);
}
.scene-bar {
  background: linear-gradient(90deg, theme('colors.primary.red-50') 0%, theme('colors.primary.red-100') 100%);
  transform-origin: left;
  animation: scene-bar-grow 600ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
@keyframes scene-bar-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .scene-bar {
    animation: none;
  }
}
</style>
